import SplashScreen from "@/components/SplashScreen";
import { useGetCustomerSetupChecklist, useUser } from "@/requests/hooks/user";
import { IFeatureFlagKeys, isFeatureFlagEnabled } from "@/utils/featureFlags";
import getCorrectHomepage from "@/utils/getCorrectHomepage";
import parseQueryString from "@/utils/parseQueryString";
import { useFlags } from "flagsmith/react";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";

interface IProps {
  children: ReactNode;
}

const ChecklistMiddleware = ({ children }: IProps) => {
  const { initialVisit } = parseQueryString();
  const { data: userData } = useUser();
  const flags = useFlags<IFeatureFlagKeys>(["web-app-mat-onboarding-enabled"]);
  const isWebAppMatOnboardingEnabled = isFeatureFlagEnabled(
    flags["web-app-mat-onboarding-enabled"],
  );

  const { data: checklistData, isLoading: isChecklistLoading } =
    useGetCustomerSetupChecklist(isWebAppMatOnboardingEnabled);

  const userHasNoChecklist = !isChecklistLoading && checklistData?.length === 0;
  const userCompletedChecklist =
    !isChecklistLoading && checklistData?.every(item => item.completed);

  /**
   * Bail as early as possible if the flag isn't enabled
   */
  if (!isWebAppMatOnboardingEnabled) {
    return <Navigate to={getCorrectHomepage(userData?.scopes)} />;
  }

  /**
   * While the checklist is loading, show the splash screen state.
   */
  if (isChecklistLoading) return <SplashScreen />;

  /**
   * We will redirect all users to the checklist page upon login / email verification
   * etc. This middleware will check if the customer should be able to see this checklist.
   * If they are, we'll then check if they have any checklist items.
   *
   * If the checks fail, we'll just redirect them to the correct homepage, as they don't need
   * to see the checklist at all.
   */
  if (
    initialVisit === "true" &&
    (userHasNoChecklist || userCompletedChecklist)
  ) {
    return <Navigate to={getCorrectHomepage(userData?.scopes)} />;
  }

  return children;
};

export default ChecklistMiddleware;
